import { useEffect, useState } from "react";
import ActionMenu from "../menus/ActionMenu";
import { canAccess } from "@utils/access";

export default function Sidebar({
  consultations,
  selectedItem,
  onSelect,
  onDelete,
  onNew,
  onShowAppointments = () => {},
  onEdit,
  isOpen = false,
  onClose = () => {},
  viewMode = "new",
}) {
  const [openMenuId, setOpenMenuId] = useState(null);
  const canClinical = canAccess("clinical");
  const canFrontdesk = canAccess("frontdesk");

  useEffect(() => {
    if (isOpen) document.body.classList.add("overflow-hidden");
    else document.body.classList.remove("overflow-hidden");
    return () => document.body.classList.remove("overflow-hidden");
  }, [isOpen]);

  const displayConsultations = consultations
    .filter(
      (consultation) => consultation.files && consultation.files.length > 0
    )
    .map((consultation) => ({
      ...consultation.files[0],
      date: consultation.date,
    }));

  return (
    <>
      <div
        className={`fixed inset-0 bg-black/40 z-40 transition-opacity lg:hidden ${
          isOpen
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
        aria-hidden="true"
      />
      <aside
        className={`
          z-50 lg:z-auto
          fixed lg:static top-0 left-0 h-full
          bg-gray-50 border-r border-gray-200 p-4
          w-72 sm:w-80 lg:w-1/5
          transition-transform duration-300 ease-in-out
          lg:translate-x-0
          ${isOpen ? "translate-x-0" : "-translate-x-full"}
        `}
        role="dialog"
        aria-modal="true"
        aria-label="Consultations Sidebar"
      >
        {/* Close button (mobile) */}
        <div className="flex items-center justify-between lg:hidden mb-3">
          <h2 className="text-base font-semibold">Menu</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-md hover:bg-gray-100"
            aria-label="Close sidebar"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24">
              <path
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {canClinical && viewMode !== "appointments" && (
          <>
            <h3 className="text-base font-bold text-gray-800 mb-3">
              Previous consultations
            </h3>

            <ul className="space-y-1 text-sm text-gray-900 max-h-[72vh] overflow-y-auto pr-1">
              {displayConsultations.length > 0 ? (
                displayConsultations.map((item, idx) => {
                  const isSelected = item.file_id === selectedItem?.file_id;
                  const menuOpen = openMenuId === item.file_id;

                  return (
                    <li
                      key={idx}
                      className={`relative flex items-center justify-between rounded-md px-2 py-2 cursor-pointer ${
                        isSelected ? "bg-gray-200" : "hover:bg-gray-100"
                      }`}
                      onClick={() => {
                        onSelect(item);
                        setOpenMenuId(null);
                        onClose();
                      }}
                    >
                      <div className="flex flex-col truncate">
                        {item.isPlaceholder ? (
                          <span className="truncate text-gray-500">
                            {item.file_name}
                          </span>
                        ) : (
                          <span className="truncate">{item.file_name}</span>
                        )}
                      </div>

                      {isSelected && !item.isPlaceholder && (
                        <div className="relative">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenMenuId((prev) =>
                                prev === item.file_id ? null : item.file_id
                              );
                            }}
                            className="p-1 text-gray-500 hover:text-gray-800"
                            aria-label="Open actions"
                          >
                            <svg
                              className="w-5 h-5"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <path
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M5 12h.01M12 12h.01M19 12h.01"
                              />
                            </svg>
                          </button>

                          {menuOpen && (
                            <ActionMenu
                              index={idx}
                              onEdit={
                                onEdit
                                  ? (e) => {
                                      e?.stopPropagation?.();
                                      onEdit(item);
                                      setOpenMenuId(null);
                                    }
                                  : undefined
                              }
                              onDelete={(e) => {
                                e?.stopPropagation?.();
                                onDelete(item);
                                setOpenMenuId(null);
                              }}
                            />
                          )}
                        </div>
                      )}
                    </li>
                  );
                })
              ) : (
                <li className="text-gray-500 italic py-2 px-2">
                  No consultations found
                </li>
              )}
            </ul>
          </>
        )}
      </aside>
    </>
  );
}
